import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { Toaster } from "sonner";
import "./styles/global.css";
import { queryClient } from "./lib/query";
import { SessionProvider } from "./lib/session";
import { ThemeProvider, useTheme } from "./lib/theme";
import { TooltipProvider } from "./components/ui/overlays";
import { router } from "./router";

/**
 * Provider order matters: theme is outermost so the toaster and everything else
 * can read it, session next so the router guard has an answer, query inside that
 * so cached data is scoped to the app instance.
 */
function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <TooltipProvider>
            <RouterProvider router={router} />
            <ThemedToaster />
          </TooltipProvider>
        </SessionProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

/** Sonner needs the resolved theme, so it reads it rather than guessing. */
function ThemedToaster() {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme}
      position="bottom-right"
      closeButton
      toastOptions={{
        classNames: {
          toast:
            "glass-overlay !rounded-[var(--admin-radius-inner)] !text-[var(--cg-content)] !text-[0.8125rem]",
          description: "!text-[var(--cg-content-muted)]",
          actionButton: "!bg-[var(--cg-house)] !text-[var(--cg-house-foreground)]",
          cancelButton: "!bg-[color-mix(in_oklab,var(--cg-content)_8%,transparent)] !text-[var(--cg-content)]",
        },
      }}
    />
  );
}

const container = document.getElementById("root");
if (!container) throw new Error("Missing #root element in index.html");

createRoot(container).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
